import {
  Stack,
  useLocalSearchParams,
  useNavigation,
  useRouter,
} from 'expo-router';
import Head from 'expo-router/head';
import { useEffect, useState } from 'react';
import { YStack } from 'tamagui';

import PSButton from '@/components/ui/PSButton';
import PSInput from '@/components/ui/PSInput';
import PSText from '@/components/ui/PSText';
import { PSErrorClass } from '@/src/api/apiError';
import authApi from '@/src/api/authApi';
import { useAuthContext } from '@/src/lib/authContext';
import { toast } from '@tamagui/toast/v2';
import { setRestoredUser, useSessionUser } from '@/src/lib/sessionStore';
import PSMobileInput from '@/components/ui/PSMobileInput';

/**
 * Auth step 3 — first name, last name. The LAST step of a fresh signup: email
 * is no longer collected here, so saving a name and closing the modal is the
 * default outcome — see the module docstring on why "done, no email field" is
 * the point of this screen now.
 *
 * NO headerLeft and no swipe-dismiss (gesture off in the layout): the mobile is
 * already verified and the user is logged in by now, so we deliberately don't let
 * them walk back across that boundary to un-verify it — it's forward-only.
 *
 * `last_name` is required by the API. First and last name both gate the button.
 *
 * ── Where this screen goes next ──
 * A booking/purchase/profile detour that needs a FULLY complete profile (not
 * just a name) pushes here with a `next: 'email'` param — on success we chain
 * straight into `/auth/email` instead of closing the modal, carrying the same
 * `headerCopy` forward. Without that param (the organic post-signup path),
 * saving the name ends the flow.
 *
 * Validation is screen-owned (the atoms are render-only): non-empty for first
 * and last name, validated on blur and cleared on focus.
 */
export default function AuthDetails() {
  const router = useRouter();
  const rootNavigation = useNavigation('/');
  const { firstName, setFirstName, lastName, setLastName } = useAuthContext();
  const [firstNameError, setFirstNameError] = useState<string | undefined>();
  const [lastNameError, setLastNameError] = useState<string | undefined>();
  const [submitting, setSubmitting] = useState(false);
  const user = useSessionUser();
  const { headerCopy, next } = useLocalSearchParams<{
    headerCopy?: string;
    next?: string;
  }>();

  const firstNameValid = firstName.trim().length > 0;
  const lastNameValid = lastName.trim().length > 0;

  useEffect(() => {
    if (user) {
      if (user.firstName) setFirstName(user.firstName);
      if (user.lastName) setLastName(user.lastName);
    }
  }, [user, setFirstName, setLastName]);

  const handleSubmit = async () => {
    if (!firstNameValid) setFirstNameError('Enter your first name');
    if (!lastNameValid) setLastNameError('Enter your last name');
    if (!firstNameValid || !lastNameValid) return;

    const trimmedFirstName = firstName.trim();
    const trimmedLastName = lastName.trim();
    setFirstName(trimmedFirstName);
    setLastName(trimmedLastName);

    setSubmitting(true);
    try {
      const { user: updatedUser } = await authApi.updateMe({
        firstName: trimmedFirstName,
        lastName: trimmedLastName,
      });
      // No later verify step to pick this up for us now that a names-only
      // save can end the flow — sync the reactive session ourselves.
      setRestoredUser(updatedUser);

      if (
        next === 'email' &&
        (updatedUser.isEmailMissing ||
          updatedUser.isOnlyEmailVerificationPending)
      ) {
        router.push({
          pathname: '/auth/email',
          params: headerCopy ? { headerCopy } : undefined,
        });
      } else {
        closeAuthModal();
      }
    } catch (err) {
      toast.error(
        err instanceof PSErrorClass
          ? err.message
          : 'Something went wrong. Please try again.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  /** See the note in verify-mobile: leaving the flow is a ROOT-stack pop. */
  const closeAuthModal = () => {
    if (rootNavigation.canGoBack()) {
      rootNavigation.goBack();
    } else {
      router.replace('/');
    }
  };

  return (
    <>
      <Head>
        <title>Your details · Putting Scene</title>
      </Head>
      <Stack.Screen
        options={{
          headerBackVisible: false,
          headerShown: true,
        }}
      />

      <YStack px="$lg" gap="$lg" pt={'$space.lg'}>
        <PSText.H1 color="$textDark">
          {headerCopy ? headerCopy : 'Please tell us more about yourself'}
        </PSText.H1>
        <YStack gap="$xs">
          <PSText.Body3 color="$textDarkMuted">Mobile</PSText.Body3>
          <PSMobileInput
            // remove +91 from mobile. Take only last 10 digits
            value={user?.mobile.slice(-10)!}
            disabled
            onChangeText={() => {}}
          />
        </YStack>
        <YStack gap="$xs">
          <PSText.Body3 color="$textDarkMuted">First name</PSText.Body3>
          <PSInput
            type="text"
            value={firstName}
            onChangeText={setFirstName}
            autoFocus
            spellCheck={false}
            autoCorrect={false}
            error={firstNameError}
            onFocus={() => setFirstNameError(undefined)}
            onBlur={() =>
              setFirstNameError(
                firstNameValid ? undefined : 'Enter your first name'
              )
            }
          />
        </YStack>
        <YStack gap="$xs">
          <PSText.Body3 color="$textDarkMuted">Last name</PSText.Body3>
          <PSInput
            type="text"
            value={lastName}
            onChangeText={setLastName}
            spellCheck={false}
            autoCorrect={false}
            error={lastNameError}
            onFocus={() => setLastNameError(undefined)}
            onBlur={() =>
              setLastNameError(
                lastNameValid ? undefined : 'Enter your last name'
              )
            }
          />
        </YStack>
        <PSButton
          title="Submit"
          loading={submitting}
          loadingTitle="Saving"
          onPress={handleSubmit}
          disabled={!firstNameValid || !lastNameValid}
        />
      </YStack>
    </>
  );
}
